import { useState, useEffect } from 'react';
import { Flag, AlertTriangle, Loader2 } from 'lucide-react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { colors, Card } from '../../shared';
import { useAuth } from '../../../contexts/AuthContext';
import {
  FEATURE_FLAG_DEFINITIONS,
  resolveFlags,
} from '../../../utils/featureFlags';

export default function FeatureFlagsTab() {
  const { user } = useAuth();
  const [flags, setFlags] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // key being saved

  // Real-time listener on feature flags doc
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'app_config', 'feature_flags'),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setFlags(resolveFlags(data));
        setMetadata(data ? { updatedAt: data.updatedAt, updatedBy: data.updatedBy } : null);
        setLoading(false);
      },
      (err) => {
        console.error('Feature flags listener error:', err);
        setFlags(resolveFlags(null));
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const handleToggle = async (key) => {
    if (!flags) return;
    setSaving(key);
    try {
      await setDoc(doc(db, 'app_config', 'feature_flags'), {
        ...flags,
        [key]: !flags[key],
        updatedAt: new Date().toISOString(),
        updatedBy: user?.email || 'unknown',
      }, { merge: true });
    } catch (err) {
      console.error('Failed to update feature flag:', err);
    } finally {
      setSaving(null);
    }
  };

  const disabledCount = flags ? Object.values(flags).filter(v => !v).length : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin" size={24} style={{ color: colors.yellow }} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl" style={{ background: colors.yellowDim }}>
          <Flag size={24} style={{ color: colors.yellow }} />
        </div>
        <div>
          <h2 className="text-xl font-bold" style={{ color: colors.textPrimary }}>
            Feature Flags
          </h2>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            Control which features technicians can see in the app
          </p>
        </div>
      </div>

      {/* Warning banner when features are disabled */}
      {disabledCount > 0 && (
        <div
          className="flex items-center gap-3 p-4 rounded-xl mb-6"
          style={{ background: colors.orangeDim, border: `1px solid ${colors.orange}` }}
        >
          <AlertTriangle size={20} style={{ color: colors.orange }} />
          <span className="text-sm" style={{ color: colors.orange }}>
            {disabledCount} feature{disabledCount !== 1 ? 's' : ''} disabled — technicians cannot see {disabledCount === 1 ? 'this feature' : 'these features'}
          </span>
        </div>
      )}

      {/* Flag toggles */}
      <div className="space-y-3">
        {FEATURE_FLAG_DEFINITIONS.map(def => {
          const enabled = flags?.[def.key] ?? true;
          const isSaving = saving === def.key;

          return (
            <Card key={def.key} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <p className="font-medium" style={{ color: colors.textPrimary }}>
                    {def.label}
                  </p>
                  <p className="text-sm mt-0.5" style={{ color: colors.textMuted }}>
                    {def.description}
                  </p>
                </div>
                <button
                  onClick={() => handleToggle(def.key)}
                  disabled={isSaving}
                  className="relative w-12 h-7 rounded-full transition-colors flex-shrink-0"
                  style={{
                    background: enabled ? colors.green : colors.border,
                    opacity: isSaving ? 0.6 : 1,
                  }}
                >
                  <div
                    className="absolute top-0.5 w-6 h-6 rounded-full transition-transform shadow-md"
                    style={{
                      background: '#fff',
                      transform: enabled ? 'translateX(22px)' : 'translateX(2px)',
                    }}
                  />
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Metadata */}
      {metadata?.updatedAt && (
        <p className="text-xs mt-6" style={{ color: colors.textMuted }}>
          Last updated: {new Date(metadata.updatedAt).toLocaleString()} by {metadata.updatedBy}
        </p>
      )}

      {/* Info note about god mode */}
      <div
        className="mt-6 p-4 rounded-xl"
        style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}
      >
        <p className="text-sm" style={{ color: colors.textMuted }}>
          God mode users (aj@snshn.co) always see all features regardless of these settings.
          Flags only affect regular technician accounts.
        </p>
      </div>
    </div>
  );
}
